import {Component, DestroyRef, inject, linkedSignal, signal, viewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {DayOfWeek, dayOfWeekValues} from '../../../../classes/enums/DayOfWeek';
import {CheckboxItem} from '../../../../shared/classes/CheckboxItem';
import {dayOfWeekParse} from '../../../../pipes/day-of-week-parse.pipe';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DataService} from '../../../../services/store/data.service';
import {BankingSettings} from '../../../../classes/models/store/BankingSettings';
import {StoreBankingSettingsService} from '../../../../services/http/store/store-banking-settings.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Data required to open the banking settings form modal. */
export interface BankingSettingsFormData {
  storeId: string;
  previousBankingSettings?: BankingSettings;
  weekExists: boolean;
}

/**
 * This component handles creating or updating banking requirements.
 */
@Component({
  selector: 'app-banking-settings-form',
  templateUrl: './banking-settings-form.component.html',
  styleUrls: ['./banking-settings-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class BankingSettingsFormComponent {

  /** A modal component of banking settings form. */
  modalComponent = viewChild<ModalComponent>('modalComponent');
  /** An identifier of a store. */
  storeId = signal<string>('');
  /** Previous store banking settings. */
  previousBankingSettings = signal<BankingSettings | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  weekExists = signal(false);

  /** `True` if request to backend is in progress. */
  loading = signal(false);
  /** A form value of start date for banking settings, linked to previous banking settings signal. */
  selectedStartDate = linkedSignal(() => this.previousBankingSettings()?.startDate ?? '');
  /** A form value of selected banking days, linked to previous banking settings signal. */
  selectedDays = linkedSignal<DayOfWeek[]>(() => this.previousBankingSettings()?.days ?? []);
  /** Checkbox items of possible banking days to select. */
  daysOptions = dayOfWeekValues().map(dayOfWeek => new CheckboxItem(dayOfWeek, dayOfWeekParse(dayOfWeek)));
  /** The first date, from which input data can be edited. */
  editDate = inject(DataService).editDate;

  private storeBankingSettingsService = inject(StoreBankingSettingsService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);

  static open(modalService: NgbModal, data: BankingSettingsFormData): NgbModalRef {
    const modalRef = modalService.open(BankingSettingsFormComponent);
    const instance = modalRef.componentInstance as BankingSettingsFormComponent;
    instance.storeId.set(data.storeId);
    instance.previousBankingSettings.set(data.previousBankingSettings);
    instance.weekExists.set(data.weekExists);
    return modalRef;
  }

  /**
   * Sends request to backend to create or update a banking requirement.
   */
  onSubmit(): void {
    this.loading.set(true);
    const bankingSettings = new BankingSettings(this.selectedStartDate(), this.selectedDays());

    if (this.weekExists()) {
      this.editBanking(bankingSettings);
    } else {
      this.addBanking(bankingSettings);
    }
  }

  private addBanking(bankingSettings: BankingSettings): void {
    this.storeBankingSettingsService.addBanking(this.storeId(), bankingSettings)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()?.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Banking collection successfully created');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editBanking(bankingSettings: BankingSettings): void {
    this.storeBankingSettingsService.editBanking(this.storeId(), bankingSettings)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()?.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Banking collection successfully edited');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  /**
   * Updates selected banking days.
   * @param days the new banking days.
   */
  onDaysChange(days: DayOfWeek[]) {
    this.selectedDays.set(days);
  }
}